import React from 'react';
import {message} from 'antd';
import ModelerX6 from '@core_x6/lib/ModelerX6';
import {diagramsApi} from '@/api/projects';
import type {ModelPatchOp} from '@/types/api';
import type {DiagramVersionState} from '@/hooks/diagram/useDiagramWs';

export type DiagramCollaborationShortcuts = {
	projectId: number;
	diagramId: number;
	versionRef: React.MutableRefObject<DiagramVersionState>;
	historyAppliedRef: React.MutableRefObject<Set<string>>;
	applyHistoryReplay?: (patch: {patches: ModelPatchOp[]}) => void;
};

export type CollaborationHistoryStatus = 'ok' | 'conflict' | 'error';

type HistoryResponse = {
	modelJson?: string | null;
	structuralVersion?: number;
	historyStackCursor?: number;
	historyStackSize?: number;
	historyActionId?: string | null;
	historyPatches?: ModelPatchOp[] | null;
};

async function applyFullModel(modeler: ModelerX6, data: HistoryResponse): Promise<boolean> {
	const raw = data.modelJson;
	if (!raw) return false;
	let json: unknown = raw;
	if (typeof raw === 'string') {
		try {
			json = JSON.parse(raw);
		} catch {
			void message.error('Некорректный JSON модели');
			return false;
		}
	}
	const viewId = modeler.getActiveViewId?.();
	await modeler.importJSON(json);
	await modeler.openView(viewId ?? undefined);
	modeler.cleanGraphHistory?.();
	return true;
}

function applyHistoryStep(
	collaboration: DiagramCollaborationShortcuts,
	modeler: ModelerX6,
	data: HistoryResponse
): boolean {
	const id = data.historyActionId ?? undefined;
	if (id && collaboration.historyAppliedRef.current.has(id)) return true;
	if (id) collaboration.historyAppliedRef.current.add(id);
	if (data.historyPatches) {
		collaboration.applyHistoryReplay?.({patches: data.historyPatches});
		return true;
	}
	return false;
}

async function runHistory(
	collaboration: DiagramCollaborationShortcuts,
	modeler: ModelerX6,
	direction: 'undo' | 'redo'
): Promise<CollaborationHistoryStatus> {
	const {projectId, diagramId, versionRef} = collaboration;
	const v = versionRef.current;
	const request = {
		expectedStructuralVersion: v.structuralVersion,
		expectedHistoryStackCursor: v.historyStackCursor,
	};
	const res =
		direction === 'undo'
			? await diagramsApi.undoModel(projectId, diagramId, request)
			: await diagramsApi.redoModel(projectId, diagramId, request);
	if (res.status === 409) {
		void message.warning('Версия диаграммы изменилась. Обновите страницу.');
		return 'conflict';
	}
	if (res.error || !res.data) {
		void message.error(res.error?.message || (direction === 'undo' ? 'Undo не удался' : 'Redo не удался'));
		return 'error';
	}
	try {
		const applied = applyHistoryStep(collaboration, modeler, res.data);
		if (!applied) {
			const fallback = await applyFullModel(modeler, res.data);
			if (!fallback) return 'error';
		}
		versionRef.current = {
			structuralVersion: res.data.structuralVersion ?? v.structuralVersion,
			historyStackCursor: res.data.historyStackCursor ?? v.historyStackCursor,
			historyStackSize: res.data.historyStackSize ?? v.historyStackSize,
		};
		return 'ok';
	} catch (err) {
		console.warn(direction === 'undo' ? '[Undo]' : '[Redo]', err);
		void message.error(direction === 'undo' ? 'Не удалось применить откат' : 'Не удалось применить повтор');
		return 'error';
	}
}

/** Откат по серверному стеку: микропатчи с historyActionId, без новой записи истории. */
export async function executeCollaborationUndo(
	collaboration: DiagramCollaborationShortcuts,
	modeler: ModelerX6
): Promise<CollaborationHistoryStatus> {
	return runHistory(collaboration, modeler, 'undo');
}

export async function executeCollaborationRedo(
	collaboration: DiagramCollaborationShortcuts,
	modeler: ModelerX6
): Promise<CollaborationHistoryStatus> {
	return runHistory(collaboration, modeler, 'redo');
}

export function canServerUndo(serverCursor: number | null | undefined): boolean {
	return (serverCursor ?? 0) > 0;
}

export function canServerRedo(
	serverCursor: number | null | undefined,
	serverSize: number | null | undefined
): boolean {
	const size = serverSize ?? 0;
	const cursor = serverCursor ?? 0;
	return size > 0 && cursor < size - 1;
}

/**
 * Ctrl+Z / Ctrl+Y ходят по серверному стеку. Шаг приходит микропатчем и не пишется в историю снова.
 */
export function useUndoRedoShortcuts(
	modelerRef: React.MutableRefObject<ModelerX6 | null>,
	enabled: boolean,
	collaboration?: DiagramCollaborationShortcuts | null
): void {
	React.useEffect(() => {
		if (!enabled || !collaboration) return;

		const onKeyDown = (e: KeyboardEvent): void => {
			if (e.target && e.target instanceof Element) {
				if (e.target.closest('input, textarea, select, [contenteditable="true"]')) {
					return;
				}
			}

			if (!e.metaKey && !e.ctrlKey) {
				return;
			}

			const modeler = modelerRef.current;
			if (!modeler) {
				return;
			}

			const version = collaboration.versionRef.current;
			const k = e.key;
			if (k === 'z' || k === 'Z') {
				e.preventDefault();
				if (e.shiftKey) {
					if (canServerRedo(version.historyStackCursor, version.historyStackSize)) {
						void executeCollaborationRedo(collaboration, modeler);
					}
				} else if (canServerUndo(version.historyStackCursor)) {
					void executeCollaborationUndo(collaboration, modeler);
				}
			} else if (k === 'y' || k === 'Y') {
				e.preventDefault();
				if (canServerRedo(version.historyStackCursor, version.historyStackSize)) {
					void executeCollaborationRedo(collaboration, modeler);
				}
			}
		};

		window.addEventListener('keydown', onKeyDown, true);
		return () => window.removeEventListener('keydown', onKeyDown, true);
	}, [enabled, collaboration, modelerRef]);
}
